import React, { ComponentProps, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from '@user-portal/wikr-components';
import Payment, { InitConfig, MessageType, SdkMessage } from '@solidgate/react-sdk';

import { selectCurrentUser } from 'store/user/selectors';
import { selectCancellationCandidate } from 'store/subscriptions/selectors';
import { selectIsMerchantLoading, selectSolidMerchantData } from 'store/payment/selectors';
import { initSolidPayment, solidValidate } from 'store/payment/actions';
import { ModalName } from 'store/modals/types';
import { openModal } from 'store/modals/actions';

import { DEFAULT_COUNTRY } from 'constants/user';
import { PRODUCT_CODES, PRODUCT_CODE_TYPES } from 'constants/product';
import { PaymentMethods } from 'constants/payment';
import { DEFAULT_CURRENCY } from 'constants/currency';

import { useModals } from 'hooks/modals';
import { useCancelSubscription } from 'hooks';

import { getValidateRecord } from './helpers';

import * as S from './styled';

const SolidPaymentForm = () => {
    const dispatch = useDispatch();
    const solidMerchantData = useSelector(selectSolidMerchantData);
    const isMerchantLoading = useSelector(selectIsMerchantLoading);
    const cancelCandidate = useSelector(selectCancellationCandidate);
    const country = useSelector(selectCurrentUser).country;
    const paymentMethod = cancelCandidate?.payment_method;
    const { onCancelSubscription } = useCancelSubscription();
    const { closeModal } = useModals();

    const appleContainerRef = useRef<HTMLDivElement | null>(null);
    const googleContainerRef = useRef<HTMLDivElement | null>(null);

    const handleError = () => {
        openModal(ModalName.errorModalWithPayment, {
            onClose: closeModal,
        });
    };

    const handleSuccess = (e: SdkMessage[MessageType.Success]) => {
        const order = {
            order_id: e.order.order_id,
            subscription_id: e.order.subscription_id ?? cancelCandidate?.external_id,
            currency: e.order.currency,
            amount: e.order.amount,
        };

        const validateRecord = getValidateRecord(cancelCandidate, order);

        dispatch(solidValidate(validateRecord));

        onCancelSubscription();
    };

    const solidFormParams: ComponentProps<typeof Payment> = {
        applePayButtonParams: {
            color: 'black',
            integrationType: 'js',
            enabled: paymentMethod === PaymentMethods.apple_pay,
        },
        googlePayButtonParams: {
            type: 'plain',
            color: 'default',
            enabled: paymentMethod === PaymentMethods.google_pay,
        },
        applePayContainerRef: appleContainerRef,
        googlePayContainerRef: googleContainerRef,
        formParams: {
            allowSubmit: true,
            autoFocus: false,
            enabled: false,
        },
        merchantData: {
            paymentIntent: solidMerchantData?.payment_intent,
            merchant: solidMerchantData?.merchant,
            signature: solidMerchantData?.signature,
        } as InitConfig['merchantData'],
        width: '100%',
        onSuccess: handleSuccess,
        onError: handleError,
        onFail: handleError,
    };

    useEffect(() => {
        dispatch(
            initSolidPayment({
                country: country || DEFAULT_COUNTRY,
                currency: cancelCandidate?.product.currency || DEFAULT_CURRENCY.name,
                price: Number(cancelCandidate?.fee?.fee_amount),
                productCode: cancelCandidate?.product_code || PRODUCT_CODES.APP_FULL_ACCESS,
                productCodeType: cancelCandidate?.product_type || PRODUCT_CODE_TYPES.MAIN_SUBSCRIPTION,
                subscriptionId: cancelCandidate?.external_id || '',
            })
        );
    }, []);

    if (isMerchantLoading || !solidMerchantData?.payment_intent) {
        return (
            <S.LoaderContainer>
                <Loader size={24} color="text-secondary" />
            </S.LoaderContainer>
        );
    }

    return (
        <>
            <Payment {...solidFormParams} />
            {paymentMethod === PaymentMethods.google_pay && (
                <S.GooglePayBtn ref={googleContainerRef} data-option="google" />
            )}
            {paymentMethod === PaymentMethods.apple_pay && (
                <S.ApplePayBtn ref={appleContainerRef} data-option="apple" />
            )}
        </>
    );
};

export default SolidPaymentForm;
